import clsx from "clsx";
import { useEffect, useRef } from "react";

import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { drawRoundedRect } from "../../../utils";

interface WaveformCanvasProps {
  isRecording: boolean;
  hasAudio: boolean;
  waveform: number[] | null;
  isPlaying: boolean;
  playbackTime: number;
  analyser?: AnalyserNode | null;
  duration?: number;
  currentTime?: number;
}

// Helper function to set up high-DPI canvas
const setupHighDPICanvas = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;

  canvas.width = width * dpr;
  canvas.height = height * dpr;

  ctx.scale(dpr, dpr);

  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  return ctx;
};

// Draw waveform for preview/playback
export const drawWaveform = (
  canvas: HTMLCanvasElement,
  waveformData: number[],
  playedRatio: number,
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const displayWidth = canvas.clientWidth;
  const displayHeight = canvas.clientHeight;

  ctx.clearRect(0, 0, displayWidth, displayHeight);

  const barWidth = 3;
  const gap = 2;

  let x = 0;

  const numBars = waveformData.length;
  const scale = 2;

  for (let i = 0; i < numBars; i += 1) {
    const barHeight = Math.min(waveformData[i] * scale, displayHeight);
    const y = (displayHeight - barHeight) / 2;
    const fillStyle = i / numBars < playedRatio ? "#642CFF" : "#C1AFFF";

    ctx.fillStyle = fillStyle;

    drawRoundedRect(ctx, x, y, barWidth, barHeight, 2);
    x += barWidth + gap;
  }
};

// Draw waveform during recording (live)
export const drawLive = (
  dataArray: Uint8Array,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
) => {
  // Get the display size in pxls
  const displayWidth = canvas.clientWidth;
  const displayHeight = canvas.clientHeight;

  ctx.clearRect(0, 0, displayWidth, displayHeight);

  const barWidth = 3;
  const gap = 2;

  const minBarHeight = 4;
  const numBars = Math.floor(displayWidth / (barWidth + gap));

  let x = 0;

  for (let i = 0; i < numBars; i += 1) {
    const start = Math.floor((i / numBars) * dataArray.length);
    const end = Math.floor(((i + 1) / numBars) * dataArray.length);

    let sum = 0;

    for (let j = start; j < end; j += 1) {
      sum += Math.abs(dataArray[j] - 128);
    }

    const avg = sum / (end - start);
    let barHeight = (avg / 128) * displayHeight * 2 || minBarHeight;

    barHeight = Math.max(barHeight, minBarHeight);

    const y = (displayHeight - barHeight) / 2;
    const fillStyle = "#ffffff";

    ctx.fillStyle = fillStyle;

    drawRoundedRect(ctx, x, y, barWidth, barHeight, 2);
    x += barWidth + gap;
  }
};

export default function WaveformCanvas({
  isRecording,
  hasAudio,
  waveform,
  isPlaying,
  playbackTime,
  analyser = null,
  duration = 0,
  currentTime = 0,
}: WaveformCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const playbackStartTimeRef = useRef<number>(0);
  const playbackStartPositionRef = useRef<number>(0);

  const isDesktop = useTwBreakpoint("lg");

  // High-DPI canvas setup
  useEffect(() => {
    if (canvasRef.current) {
      let canvasWidth = 0;

      if (isRecording || hasAudio) {
        canvasWidth = isDesktop ? 216 : 190;
      }

      setupHighDPICanvas(canvasRef.current, canvasWidth, 27);
    }
  }, [isRecording, hasAudio, isDesktop]);

  // Waveform live drawing
  useEffect(() => {
    if (!isRecording || !analyser || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    let raf: number;
    const draw = () => {
      analyser.getByteTimeDomainData(dataArray);
      drawLive(dataArray, canvas, ctx);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => raf && cancelAnimationFrame(raf);
  }, [isRecording, analyser]);

  // Playback waveform drawing
  useEffect(() => {
    if (isRecording || !hasAudio || !waveform) return;

    let raf: number;

    const update = () => {
      if (!canvasRef.current) return;

      const dur = duration && duration > 0 ? duration : 1;
      let currentPosition: number;

      if (isPlaying) {
        const elapsed =
          (performance.now() - playbackStartTimeRef.current) / 1000;
        currentPosition = Math.min(
          playbackStartPositionRef.current + elapsed,
          dur,
        );
      } else {
        currentPosition =
          typeof currentTime === "number" ? currentTime : playbackTime;
      }

      const playedRatio =
        currentPosition > 0 ? Math.min(currentPosition / dur, 1) : 0;
      drawWaveform(canvasRef.current, waveform, playedRatio);

      if (isPlaying) {
        raf = requestAnimationFrame(update);
      }
    };

    // When playing start the animation; when paused show the current position
    if (isPlaying) {
      playbackStartTimeRef.current = performance.now();
      playbackStartPositionRef.current =
        typeof currentTime === "number" ? currentTime : playbackTime;
      update();
    } else if (canvasRef.current) {
      const dur = duration && duration > 0 ? duration : 1;
      const cur = typeof currentTime === "number" ? currentTime : playbackTime;
      const playedRatio = cur > 0 ? Math.min(cur / dur, 1) : 0;
      drawWaveform(canvasRef.current, waveform, playedRatio);
    }

    return () => raf && cancelAnimationFrame(raf);
  }, [
    isPlaying,
    hasAudio,
    waveform,
    isRecording,
    playbackTime,
    duration,
    currentTime,
  ]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    },
    [],
  );

  return (
    <canvas
      ref={canvasRef}
      className={clsx([
        "transition-all duration-300",
        isRecording || hasAudio ? "w-[190px] lg:w-[216px]" : "w-0",
      ])}
    />
  );
}
